'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Warehouse,
  Tag,
  TrendingUp,
  Megaphone,
  RotateCcw,
  FileText,
  AlertTriangle,
  Upload,
  Link2,
  Users,
  Settings,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  BadgeDollarSign,
  Shield,
  LogOut,
  CalendarClock,
  Building2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { canView, hrefToModuleKey } from '@/lib/rbac';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
  group?: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/', group: 'main' },
  { id: 'nav-sales', label: 'Sales & Revenue', icon: ShoppingCart, href: '/sales-revenue', group: 'main' },
  { id: 'nav-inventory', label: 'Inventory', icon: Package, href: '/inventory-management', badge: 3, group: 'main' },
  { id: 'nav-warehouses', label: 'Warehouses', icon: Warehouse, href: '/warehouses', group: 'main' },
  { id: 'nav-products', label: 'Products', icon: Tag, href: '/products', group: 'main' },
  { id: 'nav-profitability', label: 'Profitability', icon: TrendingUp, href: '/profitability', group: 'analytics' },
  { id: 'nav-advertising', label: 'Advertising', icon: Megaphone, href: '/advertising', group: 'analytics' },
  { id: 'nav-returns', label: 'Returns', icon: RotateCcw, href: '/returns', badge: 12, group: 'analytics' },
  { id: 'nav-listings', label: 'Listings', icon: FileText, href: '/listings', group: 'analytics' },
  { id: 'nav-unauthorized', label: 'Unauth. Sellers', icon: Shield, href: '/unauthorized-sellers', group: 'analytics' },
  { id: 'nav-settlements', label: 'Settlements', icon: BadgeDollarSign, href: '/settlements', group: 'analytics' },
  { id: 'nav-reports', label: 'Reports', icon: ClipboardList, href: '/reports', group: 'operations' },
  { id: 'nav-alerts', label: 'Alerts', icon: AlertTriangle, href: '/alerts', badge: 5, group: 'operations' },
  { id: 'nav-upload', label: 'Upload Centre', icon: Upload, href: '/upload-centre', group: 'operations' },
  { id: 'nav-import-schedules', label: 'Import Schedules', icon: CalendarClock, href: '/import-schedules', group: 'operations' },
  { id: 'nav-integrations', label: 'Integrations', icon: Link2, href: '/integrations', group: 'operations' },
  { id: 'nav-users', label: 'Users', icon: Users, href: '/users', group: 'system' },
  { id: 'nav-workspace', label: 'Workspace', icon: Building2, href: '/workspace', group: 'system' },
  { id: 'nav-settings', label: 'Settings', icon: Settings, href: '/settings', group: 'system' },
  { id: 'nav-audit', label: 'Audit Logs', icon: ClipboardList, href: '/audit-logs', group: 'system' },
];

const groups = [
  { id: 'main', label: 'Operations' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'operations', label: 'Management' },
  { id: 'system', label: 'System' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, role, signOut } = useAuth();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const isVisible = (href: string) => {
    const moduleKey = hrefToModuleKey(href);
    if (!moduleKey) return true;
    return canView(role, moduleKey);
  };

  const handleSignOut = async () => {
    await signOut();
    router.replace('/login');
  };

  const roleLabel = role
    ? role.charAt(0).toUpperCase() + role.slice(1)
    : '';

  const userInitials = user?.email
    ? user.email.slice(0, 2).toUpperCase()
    : 'U';

  const displayName = user?.user_metadata?.full_name || user?.email || 'User';

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-card border-r border-border flex flex-col z-30 sidebar-transition ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center border-b border-border flex-shrink-0 ${collapsed ? 'h-16 justify-center px-0' : 'h-16 px-4 gap-3'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-700 text-foreground leading-tight truncate">NTNY BI Portal</span>
            <span className="text-2xs text-muted-foreground truncate">Radsan International</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3">
        {groups.map((group) => {
          const items = navItems.filter((i) => i.group === group.id && isVisible(i.href));
          if (items.length === 0) return null;
          return (
            <div key={`group-${group.id}`} className="mb-1">
              {!collapsed && (
                <p className="px-4 py-1.5 text-2xs font-600 uppercase tracking-widest text-muted-foreground">
                  {group.label}
                </p>
              )}
              {items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`flex items-center gap-3 mx-2 my-0.5 rounded-lg text-sm font-500 transition-all duration-150 btn-press relative group
                      ${collapsed ? 'justify-center px-0 py-2.5 h-10' : 'px-3 py-2'}
                      ${active ? 'nav-active' : 'nav-inactive'}
                    `}
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                    {!collapsed && item.badge && item.badge > 0 ? (
                      <span className="ml-auto bg-negative text-white text-2xs font-600 rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none">
                        {item.badge}
                      </span>
                    ) : null}
                    {collapsed && item.badge && item.badge > 0 ? (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-negative rounded-full" />
                    ) : null}
                    {collapsed && (
                      <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
                        {item.label}
                      </span>
                    )}
                  </Link>
                );
              })}
              {!collapsed && <div className="mx-4 mt-1 mb-0 border-t border-border/50" />}
            </div>
          );
        })}
      </nav>

      {/* Toggle + User */}
      <div className="border-t border-border flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2 px-4 py-3">
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-700 flex-shrink-0">
              {userInitials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-600 text-foreground truncate">{displayName}</p>
              <p className="text-2xs text-muted-foreground truncate">{roleLabel}</p>
            </div>
            <button
              onClick={handleSignOut}
              title="Sign out"
              className="p-1.5 rounded text-muted-foreground hover:text-negative hover:bg-negative-bg transition-colors btn-press flex-shrink-0"
            >
              <LogOut size={13} />
            </button>
          </div>
        )}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center py-3 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150 btn-press"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : (
            <span className="flex items-center gap-2 text-xs">
              <ChevronLeft size={14} />
              <span>Collapse</span>
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}